import React, { useCallback, useMemo, useState } from 'react';

import dayjs from '~/timeInit';

import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { formatDateShort, toSortableTimestamp } from '~/services/TimeService';

import type { SalesforceSurchargePeriod } from '~/types/surcharges';

import currencyFormatter from '~/utils/currencyFormatter';
import { daysOfWeekMessage } from '~/utils/daysOfWeek';

import TabPanelShell from '../../../shared/TabPanelShell';
import { useDealInformationTabCount } from '../../DealInformationTabCounts';
import SortableTableHeaderCell from '../../shared/SortableTableHeaderCell';
import { getDateRangeAvailability } from '../../shared/dateRangeAvailability';
import { useFilteredByExpiry } from '../../shared/useFilteredByExpiry';
import { useTableSort } from '../../shared/useTableSort';
import type { SurchargeRoomAssignment } from '../../types';

import BooleanChip from './BooleanChip';
import MatchedRoomRateCards from './MatchedRoomRateCards';
import UnmatchedRoomRateCards from './UnmatchedRoomRateCards';
import {
  SURCHARGES_TABLE_COLUMN_COUNT,
  UNMATCHED_ROW_ID,
  dropdownContentSx,
  getDropdownCellSx,
  getUniqueRoomRateCount,
  handleExpandRowKeyDown,
  toggleExpandedRow,
} from './surchargeTabLayout';
import {
  buildAppliedRoomsBySurchargeId,
  getApplicableNightCount,
  getUnmatchedReservationSurcharges,
  groupReservationSurcharges,
} from './surchargeUtils';
import type { SurchargesTabProps } from './types';

type SurchargeSortField =
  | 'name'
  | 'start_date'
  | 'end_date'
  | 'daysOfWeek'
  | 'nights'
  | 'cost'
  | 'convertedCost'
  | 'sellPrice'
  | 'margin'
  | 'collection'
  | 'accumulate'
  | 'overlapping'
  | 'matchingRoomRates';

interface SurchargeSortRow {
  matchingAssignments: Array<SurchargeRoomAssignment>;
  surcharge: SalesforceSurchargePeriod;
}

export default function SurchargesTab({
  canAddSurcharges,
  error,
  isLoading,
  isLoadingReservationSurcharges,
  onAddSurcharges,
  reservationSurcharges,
  reservationSurchargesError,
  surcharges,
}: SurchargesTabProps) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [hideExpired, setHideExpired] = useState(true);
  useDealInformationTabCount('surcharges', isLoading || error ? undefined : surcharges.length);
  const appliedRoomsBySurchargeId = useMemo(
    () =>
      buildAppliedRoomsBySurchargeId({
        reservationSurcharges,
        surcharges,
      }),
    [reservationSurcharges, surcharges],
  );
  const unmatchedReservationSurcharges = useMemo(
    () => getUnmatchedReservationSurcharges(reservationSurcharges),
    [reservationSurcharges],
  );
  const unmatchedRoomRateCount = useMemo(
    () =>
      groupReservationSurcharges(unmatchedReservationSurcharges).reduce(
        (count, room) => count + room.rateGroups.length,
        0,
      ),
    [unmatchedReservationSurcharges],
  );
  const now = dayjs.tz();
  const isExpired = useCallback(
    (surcharge: SalesforceSurchargePeriod) => getDateRangeAvailability(surcharge, now) === 'expired',
    [now],
  );
  const visibleSurcharges = useFilteredByExpiry(surcharges, isExpired, hideExpired);
  const surchargeSortRows = useMemo<Array<SurchargeSortRow>>(
    () =>
      visibleSurcharges.map((surcharge) => ({
        matchingAssignments: appliedRoomsBySurchargeId[surcharge.id] ?? [],
        surcharge,
      })),
    [visibleSurcharges, appliedRoomsBySurchargeId],
  );
  const getSurchargeSortValue = useCallback((row: SurchargeSortRow, field: SurchargeSortField) => {
    const { matchingAssignments, surcharge } = row;
    switch (field) {
      case 'name':
        return surcharge.name;
      case 'start_date':
        return toSortableTimestamp(surcharge.start_date);
      case 'end_date':
        return toSortableTimestamp(surcharge.end_date);
      case 'daysOfWeek':
        return daysOfWeekMessage(surcharge);
      case 'nights':
        return getApplicableNightCount(surcharge);
      case 'cost':
        return surcharge.cost_price ?? null;
      case 'convertedCost':
        return surcharge.converted_cost ?? null;
      case 'sellPrice':
        return surcharge.sell_price ?? null;
      case 'margin':
        return typeof surcharge.margin === 'number' ? surcharge.margin : null;
      case 'collection':
        return surcharge.collected_by;
      case 'accumulate':
        return surcharge.accumulate ? 1 : 0;
      case 'overlapping':
        return surcharge.overlapping ? 1 : 0;
      case 'matchingRoomRates':
        return getUniqueRoomRateCount(matchingAssignments);
      default:
        return null;
    }
  }, []);
  const {
    sortField,
    sortDirection,
    handleSortChange,
    sortedRows: sortedSurchargeRows,
  } = useTableSort(surchargeSortRows, getSurchargeSortValue, 'start_date');

  if (isLoading) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
        <CircularProgress />
        <Typography color="text.secondary">Loading surcharges.</Typography>
      </Stack>
    );
  }

  if (error) {
    return <Alert severity="error">Unable to load Salesforce surcharges.</Alert>;
  }

  return (
    <TabPanelShell
      title="Surcharges"
      actions={
        <Button
          disabled={!canAddSurcharges}
          onClick={onAddSurcharges}
          startIcon={<AddIcon />}
          sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
          variant="contained"
        >
          Apply Salesforce surcharges
        </Button>
      }
      contentSx={{ p: 0 }}
      titleActions={
        <Button
          size="small"
          variant="outlined"
          startIcon={hideExpired ? <VisibilityIcon /> : <VisibilityOffIcon />}
          onClick={() => setHideExpired((current) => !current)}
          sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
        >
          {hideExpired ? 'Show expired blocks' : 'Hide expired blocks'}
        </Button>
      }
    >
      <Stack spacing={1} sx={{ px: 3, pt: 3, pb: 1.5 }}>
        <Typography color="text.secondary" variant="body2">
          Expand a Salesforce surcharge to see the room rates with a matching surcharge block. Applying Salesforce
          surcharges replaces the existing blocks on each room rate you select, including blocks with no matching
          Salesforce surcharge on this opportunity.
        </Typography>
      </Stack>
      <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto', overflowY: 'hidden' }}>
        <Table size="small" sx={{ minWidth: 1300 }}>
          <TableHead>
            <TableRow
              sx={{
                '& .MuiTableCell-head': {
                  bgcolor: (theme) => alpha(theme.palette.info.main, 0.14),
                  color: 'info.dark',
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 0,
                  textTransform: 'uppercase',
                },
              }}
            >
              <TableCell colSpan={SURCHARGES_TABLE_COLUMN_COUNT}>Parent Salesforce surcharges</TableCell>
            </TableRow>
            <TableRow
              sx={{
                '& .MuiTableCell-head': {
                  bgcolor: (theme) => alpha(theme.palette.info.main, 0.06),
                  color: 'info.dark',
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 0,
                  textTransform: 'uppercase',
                },
              }}
            >
              <TableCell />
              <SortableTableHeaderCell
                field="name"
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
              >
                Name
              </SortableTableHeaderCell>
              <SortableTableHeaderCell
                field="start_date"
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
              >
                From
              </SortableTableHeaderCell>
              <SortableTableHeaderCell
                field="end_date"
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
              >
                To
              </SortableTableHeaderCell>
              <SortableTableHeaderCell
                field="daysOfWeek"
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
              >
                Days of week
              </SortableTableHeaderCell>
              <SortableTableHeaderCell
                align="center"
                field="nights"
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
              >
                Nights
              </SortableTableHeaderCell>
              <SortableTableHeaderCell
                align="right"
                field="cost"
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
              >
                Cost
              </SortableTableHeaderCell>
              <SortableTableHeaderCell
                align="right"
                field="convertedCost"
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
              >
                Converted Cost
              </SortableTableHeaderCell>
              <SortableTableHeaderCell
                align="right"
                field="sellPrice"
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
              >
                Sell Price
              </SortableTableHeaderCell>
              <SortableTableHeaderCell
                align="right"
                field="margin"
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
              >
                Margin
              </SortableTableHeaderCell>
              <SortableTableHeaderCell
                field="collection"
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
              >
                Collection
              </SortableTableHeaderCell>
              <SortableTableHeaderCell
                align="center"
                field="accumulate"
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
              >
                Accumulate
              </SortableTableHeaderCell>
              <SortableTableHeaderCell
                align="center"
                field="overlapping"
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
              >
                Overlapping
              </SortableTableHeaderCell>
              <SortableTableHeaderCell
                align="center"
                field="matchingRoomRates"
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
              >
                Matching room rates
              </SortableTableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {surcharges.length === 0 && (
              <TableRow>
                <TableCell colSpan={SURCHARGES_TABLE_COLUMN_COUNT}>
                  <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                    No Salesforce surcharges found for this offer.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {surcharges.length > 0 && visibleSurcharges.length === 0 && (
              <TableRow>
                <TableCell colSpan={SURCHARGES_TABLE_COLUMN_COUNT}>
                  <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                    All Salesforce surcharges for this offer are expired.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {sortedSurchargeRows.map(({ matchingAssignments, surcharge }) => {
              const margin = surcharge.margin;
              const hasMargin = typeof margin === 'number';
              const isExpanded = expandedRows.has(surcharge.id);

              return (
                <React.Fragment key={surcharge.id}>
                  <TableRow
                    hover
                    role="button"
                    sx={{ cursor: 'pointer' }}
                    tabIndex={0}
                    onClick={() => setExpandedRows((current) => toggleExpandedRow(current, surcharge.id))}
                    onKeyDown={(event) =>
                      handleExpandRowKeyDown(event, () =>
                        setExpandedRows((current) => toggleExpandedRow(current, surcharge.id)),
                      )
                    }
                  >
                    <TableCell>{isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}</TableCell>
                    <TableCell>{surcharge.name}</TableCell>
                    <TableCell>{formatDateShort(surcharge.start_date)}</TableCell>
                    <TableCell>{formatDateShort(surcharge.end_date)}</TableCell>
                    <TableCell>{daysOfWeekMessage(surcharge)}</TableCell>
                    <TableCell align="center">{getApplicableNightCount(surcharge)}</TableCell>
                    <TableCell align="right">
                      {currencyFormatter(surcharge.currency_code, surcharge.cost_price)}
                    </TableCell>
                    <TableCell align="right">{currencyFormatter('AUD', surcharge.converted_cost)}</TableCell>
                    {/* Salesforce Price__c is always AUD; currency_code (CurrencyIsoCode) describes Cost__c only. */}
                    <TableCell align="right">{currencyFormatter('AUD', surcharge.sell_price)}</TableCell>
                    <TableCell align="right">
                      <Typography
                        color={hasMargin ? (margin >= 20 ? 'success.main' : 'warning.main') : 'error.main'}
                        fontWeight={700}
                        variant="body2"
                      >
                        {hasMargin ? `${margin.toFixed(1)}%` : 'Missing'}
                      </Typography>
                    </TableCell>
                    <TableCell>{surcharge.collected_by}</TableCell>
                    <TableCell align="center">
                      <BooleanChip value={surcharge.accumulate} />
                    </TableCell>
                    <TableCell align="center">
                      <BooleanChip value={surcharge.overlapping} />
                    </TableCell>
                    <TableCell align="center">
                      {isLoadingReservationSurcharges ? (
                        <CircularProgress size={16} />
                      ) : (
                        <Chip label={getUniqueRoomRateCount(matchingAssignments)} size="small" variant="outlined" />
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={SURCHARGES_TABLE_COLUMN_COUNT} sx={getDropdownCellSx(isExpanded)}>
                      <Collapse unmountOnExit in={isExpanded} sx={{ width: '100%' }} timeout="auto">
                        <Box sx={dropdownContentSx}>
                          {reservationSurchargesError ? (
                            <Alert severity="error">Unable to load room-rate surcharges.</Alert>
                          ) : (
                            <MatchedRoomRateCards
                              assignments={matchingAssignments}
                              hideExpired={hideExpired}
                              now={now}
                            />
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
            <TableRow
              hover
              role="button"
              sx={{
                cursor: 'pointer',
                '& .MuiTableCell-root': { bgcolor: 'grey.50' },
              }}
              tabIndex={0}
              onClick={() => setExpandedRows((current) => toggleExpandedRow(current, UNMATCHED_ROW_ID))}
              onKeyDown={(event) =>
                handleExpandRowKeyDown(event, () =>
                  setExpandedRows((current) => toggleExpandedRow(current, UNMATCHED_ROW_ID)),
                )
              }
            >
              <TableCell>
                {expandedRows.has(UNMATCHED_ROW_ID) ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
              </TableCell>
              <TableCell colSpan={SURCHARGES_TABLE_COLUMN_COUNT - 2}>
                <Typography fontWeight={700} variant="body2">
                  Room Rate Surcharges with No Matching Salesforce Surcharge
                </Typography>
              </TableCell>
              <TableCell align="center">
                {isLoadingReservationSurcharges ? (
                  <CircularProgress size={16} />
                ) : (
                  <Chip label={unmatchedRoomRateCount} size="small" variant="outlined" />
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell
                colSpan={SURCHARGES_TABLE_COLUMN_COUNT}
                sx={getDropdownCellSx(expandedRows.has(UNMATCHED_ROW_ID))}
              >
                <Collapse unmountOnExit in={expandedRows.has(UNMATCHED_ROW_ID)} sx={{ width: '100%' }} timeout="auto">
                  <Box sx={dropdownContentSx}>
                    {isLoadingReservationSurcharges ? (
                      <Stack alignItems="center" direction="row" justifyContent="center" spacing={1} sx={{ py: 2 }}>
                        <CircularProgress size={16} />
                        <Typography color="text.secondary" variant="body2">
                          Loading room-rate surcharges.
                        </Typography>
                      </Stack>
                    ) : reservationSurchargesError ? (
                      <Alert severity="error">Unable to load room-rate surcharges.</Alert>
                    ) : (
                      <UnmatchedRoomRateCards
                        assignments={unmatchedReservationSurcharges}
                        hideExpired={hideExpired}
                        now={now}
                      />
                    )}
                  </Box>
                </Collapse>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </TabPanelShell>
  );
}
